import gql from 'graphql-tag';

import { SITE_IDENTIFIER } from '@src/typescriptGlobals/constants';

export const GQL_FETCH_PAGE_METADATA_ENTRY = gql`
  query ($locale: String!, $slug: String!, $preview: Boolean!) {
    page: pageCollection(
      limit: 1
      where: {
        contentfulMetadata: { tags: { id_contains_some: ["${SITE_IDENTIFIER}"] } },
        languageCode: $locale,
        slug: $slug
      }
      preview: $preview
    ) {
      items {
        slug
        languageCode
        seoMetadata {
          title
          description
          previewImage {
            url
          }
          index
          follow
        }
      }
    }

    translatedPages: pageCollection(
      limit: 1
      where: {
        contentfulMetadata: { tags: { id_contains_some: ["${SITE_IDENTIFIER}"] } },
        languageCode: "EN",
        slug: $slug
      }
      preview: $preview
    ) {
      items {
        translationsCollection(limit: 7) {
          items {
            sys {
              id
              firstPublishedAt
            }
            pageName
            slug
            languageCode
          }
        }
      }
    }
  }
`;

export const GQL_FETCH_SITEMAPS_DATA = gql`
  query ($preview: Boolean!) {
    postCollection(
      preview: $preview
      where: {
        contentfulMetadata: {
          tags: { id_contains_some: ["${SITE_IDENTIFIER}"], id_contains_none: ["translation"] }
        }
      }
    ) {
      items {
        slug
        languageCode
        sys {
          publishedAt
        }
        translationsCollection {
          items {
            languageCode
            slug
          }
        }
      }
    }
    pageCollection(
      preview: $preview
      where: {
        contentfulMetadata: {
          tags: { id_contains_some: ["${SITE_IDENTIFIER}"], id_contains_none: ["translation"] }
        }
      }
    ) {
      items {
        slug
        languageCode
        sys {
          publishedAt
        }
      }
    }
  }
`;

// =============================================
//              CANONICAL QUERY
// =============================================
export const GQL_FETCH_CANONICAL_DATA = gql`
  query {
    postCollection(
      where: {
        contentfulMetadata: {
          tags: { id_contains_some: ["${SITE_IDENTIFIER}"], id_contains_none: ["translation"] }
        }
      }
    ) {
      items {
        slug
        seoMetadata {
          canonical
        }
        translationsCollection(limit: 7) {
          items {
            languageCode
            slug
            seoMetadata {
              canonical
            }
          }
        }
      }
    }
    pageCollection(
      where: {
        contentfulMetadata: {
          tags: { id_contains_some: ["${SITE_IDENTIFIER}"], id_contains_none: ["translation"] }
        }
      }
    ) {
      items {
        slug
        seoMetadata {
          canonical
        }
      }
    }
  }
`;

export const GQL_FETCH_TOP_NAVIGATION_ENTRY = gql`
  query ($locale: String!, $preview: Boolean!) {
    topNavigation: topNavigationCollection(
      limit: 1
      where: { contentfulMetadata: { tags: { id_contains_some: ["${SITE_IDENTIFIER}"] } }, languageCode: $locale }
      preview: $preview
    ) {
      items {
        logo {
          url
          width
          height
          description
        }
        navItemsCollection(order: order_ASC) {
          items {
            sys {
              id
            }
            slug
            pageName
            languageCode
            subPagesCollection(order: order_ASC) {
              items {
                sys {
                  id
                }
                languageCode
                slug
                pageName
                description
                isNew
              }
            }
          }
        }
      }
    }

    globalTextBlock: globalTextBlocksCollection(
      limit: 1
      where: { languageCode: $locale, contentfulMetadata: { tags: { id_contains_some: ["${SITE_IDENTIFIER}"] } } }
    ) {
      items {
        allProducts
        pages
        blogArticles
        lastUpdated
        noResultsFor
        languages
      }
    }
  }
`;

export const GQL_FETCH_FOOTER_NAVIGATION_ENTRY = gql`
  query ($locale: String!, $preview: Boolean!) {
    footer: footerCollection(
      limit: 1
      where: { contentfulMetadata: { tags: { id_contains_some: ["${SITE_IDENTIFIER}"] } }, languageCode: $locale }
      preview: $preview
    ) {
      items {
        logo {
          url
          width
          height
          description
        }
        navItemsCollection(order: order_ASC) {
          items {
            sys {
              id
            }
            slug
            pageName
            subPagesCollection(order: order_ASC) {
              items {
                sys {
                  id
                }
                slug
                pageName
                description
              }
            }
          }
        }
        health
        performance
        tactical
        copyright
        allRightsReserved
        privacyPolicy
        loginToValdHub
      }
    }
  }
`;

export const GQL_FETCH_TOTAL_POST_ENTRY = gql`
  query ($preview: Boolean!) {
    postCollection(
      preview: $preview
      where: { contentfulMetadata: { tags: { id_contains_some: ["${SITE_IDENTIFIER}"] } } }
    ) {
      total
    }
  }
`;

export const GQL_FETCH_ANNOUNCEMENT_ENTRIES = gql`
  query ($locale: String!, $preview: Boolean!) {
    eBooks: announcementCollection(
      limit: 1
      where: { languageCode: $locale, contentfulMetadata: { tags: { id_contains_some: ["${SITE_IDENTIFIER}"] } } }
      preview: $preview
    ) {
      items {
        title
        topicCollection {
          items {
            sys {
              id
            }
            bodyCopy {
              json
            }
          }
        }
      }
    }
  }
`;

export const GQL_FETCH_LIVE_PREVIEW_PAGE_ENTRY = gql`
  query ($entryId: String!) {
    pageCollection(
      where: { sys: { id: $entryId }, contentfulMetadata: { tags: { id_contains_some: ["${SITE_IDENTIFIER}"] } } }
      limit: 1
      preview: true
    ) {
      items {
        pageName
        slug
        languageCode
      }
    }
  }
`;

export const QGL_PAGINATED_POST_ENTRIES = gql`
  query (
    $skip: Int!
    $limit: Int
    $filters: [PostFilter!]
    $locale: String!
    $slug: String!
    $preview: Boolean!
  ) {
    page: pageCollection(
      limit: 1
      where: {
        slug: $slug,
        languageCode: $locale,
        contentfulMetadata: { tags: { id_contains_some: ["${SITE_IDENTIFIER}"] } }
      }
      preview: $preview
    ) {
      items {
        slug
        pageName
        sys {
          publishedAt
          firstPublishedAt
        }
      }
    }

    blogs: postCollection(
      skip: $skip
      limit: $limit
      order: datePublished_DESC
      preview: $preview
      where: {
        languageCode: $locale,
        OR: $filters,
        contentfulMetadata: { tags: { id_contains_some: ["${SITE_IDENTIFIER}"] } }
      }
    ) {
      total
      items {
        sys {
          id
        }
        slug
        categoriesV2Collection {
          items {
            title
            slug
          }
        }
        title
        thumbnail {
          url
          width
          height
          title
          description
          contentType
        }
        datePublished
        body {
          json
        }
        featuredImage {
          url
        }
        excerpt
      }
    }

    globalTextBlock: globalTextBlocksCollection(
      limit: 1
      where: { languageCode: $locale, contentfulMetadata: { tags: { id_contains_some: ["${SITE_IDENTIFIER}"] } } }
    ) {
      items {
        learnMore
        noDataFound
      }
    }
  }
`;

export const GQL_FETCH_NEWS_AND_RESEARCH_PAGE_TITLE = gql`
  query ($locale: String!, $slug: String!, $preview: Boolean!) {
    page: pageCollection(
      limit: 1
      where: {
        contentfulMetadata: { tags: { id_contains_some: ["${SITE_IDENTIFIER}"] } },
        languageCode: $locale,
        slug: $slug
      }
      preview: $preview
    ) {
      items {
        slug
        pageName
        sys {
          publishedAt
          firstPublishedAt
        }
      }
    }

    globalTextBlock: globalTextBlocksCollection(
      limit: 1
      where: { languageCode: $locale, contentfulMetadata: { tags: { id_contains_some: ["${SITE_IDENTIFIER}"] } } }
    ) {
      items {
        learnMore
        noDataFound
        filterBy
        topic
        category
        products
        search
      }
    }
  }
`;

export const GQL_FETCH_NEWS_AND_RESEARCH_PAGE_ENTRIES = gql`
  query (
    $skip: Int
    $limit: Int
    $filters: [PostFilter!]
    $excludeIds: [String]
    $locale: String!
    $preview: Boolean!
  ) {
    blogs: postCollection(
      skip: $skip
      limit: $limit
      order: datePublished_DESC
      preview: $preview
      where: {
        languageCode: $locale
        OR: $filters
        contentfulMetadata: { tags: { id_contains_some: ["${SITE_IDENTIFIER}"] } }
        sys: { id_not_in: $excludeIds }
      }
    ) {
      total
      items {
        sys {
          id
        }
        contentfulMetadata {
          tags {
            name
          }
        }
        slug
        categoriesV2Collection {
          items {
            title
            slug
          }
        }
        productsCollection {
          items {
            slug
            name
          }
        }
        topicsCollection {
          items {
            title
            slug
          }
        }
        title
        thumbnail {
          url
          width
          height
          title
          description
          contentType
        }
        datePublished
        body {
          json
        }
        featuredImage {
          url
        }
        excerpt
        languageCode
        translationsCollection(limit: 7) {
          items {
            sys {
              id
              firstPublishedAt
            }
            title
            slug
            languageCode
          }
        }
        linkedFrom {
          postCollection(limit: 7) {
            items {
              sys {
                id
              }
              languageCode
              title
              slug
              translationsCollection(limit: 7) {
                items {
                  sys {
                    id
                    firstPublishedAt
                  }
                  languageCode
                  slug
                }
              }
            }
          }
        }
      }
    }

    categories: categoryV2Collection(
      preview: $preview
      order: order_ASC
      where: { languageCode: $locale, contentfulMetadata: { tags: { id_contains_some: ["${SITE_IDENTIFIER}"] } } }
    ) {
      items {
        sys {
          id
        }
        title
        slug
      }
    }

    products: productCollection(
      preview: $preview
      order: order_ASC
      where: { languageCode: $locale, contentfulMetadata: { tags: { id_contains_some: ["${SITE_IDENTIFIER}"] } } }
    ) {
      items {
        sys {
          id
        }
        title
        slug
        name
      }
    }

    topics: topicCollection(
      preview: $preview
      order: order_ASC
      where: { languageCode: $locale, contentfulMetadata: { tags: { id_contains_some: ["${SITE_IDENTIFIER}"] } } }
    ) {
      items {
        sys {
          id
        }
        title
        slug
      }
    }
  }
`;

export const GQL_FETCH_LATEST_BLOG_ENTRIES = gql`
  query ($locale: String!, $preview: Boolean!) {
    latestBlog: postCollection(
      limit: 1
      preview: $preview
      order: datePublished_DESC
      where: { languageCode: $locale, contentfulMetadata: { tags: { id_contains_some: ["${SITE_IDENTIFIER}"] } } }
    ) {
      items {
        sys {
          id
        }
        slug
        categoriesV2Collection {
          items {
            title
            slug
            title
          }
        }
        productsCollection {
          items {
            slug
            name
          }
        }
        topicsCollection {
          items {
            title
            slug
          }
        }
        title
        thumbnail {
          url
          width
          height
          title
          description
          contentType
        }
        datePublished
        excerpt
        languageCode
        translationsCollection(limit: 7) {
          items {
            sys {
              id
              firstPublishedAt
            }
            title
            slug
            languageCode
          }
        }
        linkedFrom {
          postCollection(limit: 7) {
            items {
              sys {
                id
              }
              languageCode
              title
              slug
              translationsCollection(limit: 7) {
                items {
                  sys {
                    id
                    firstPublishedAt
                  }
                  languageCode
                  slug
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const GQL_FETCH_FEATURED_BLOG_ENTRIES = gql`
  query ($locale: String!, $preview: Boolean!) {
    featuredBlog: featuredPostCollection(
      limit: 1
      preview: $preview
      where: { languageCode: $locale, contentfulMetadata: { tags: { id_contains_some: ["${SITE_IDENTIFIER}"] } } }
    ) {
      items {
        postCollection(limit: 5) {
          items {
            sys {
              id
            }
            slug
            categoriesV2Collection {
              items {
                title
                slug
              }
            }
            productsCollection {
              items {
                slug
                name
              }
            }
            topicsCollection {
              items {
                title
                slug
              }
            }
            title
            thumbnail {
              url
              width
              height
              title
              description
              contentType
            }
            datePublished
            excerpt
            languageCode
            translationsCollection(limit: 7) {
              items {
                sys {
                  id
                  firstPublishedAt
                }
                title
                slug
                languageCode
              }
            }
            linkedFrom {
              postCollection(limit: 7) {
                items {
                  sys {
                    id
                  }
                  languageCode
                  title
                  slug
                  translationsCollection(limit: 7) {
                    items {
                      sys {
                        id
                        firstPublishedAt
                      }
                      languageCode
                      slug
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const GQL_FETCH_LLMS_DATA = gql`
  query ($preview: Boolean!) {
    posts: postCollection(
      preview: $preview
      where: {
        contentfulMetadata: {
          tags: { id_contains_some: ["${SITE_IDENTIFIER}"], id_contains_none: ["translation"] }
        }
      }
    ) {
      items {
        slug
        languageCode
        sys {
          publishedAt
        }
        seoMetadata {
          title
          description
        }
      }
    }
    pages: pageCollection(
      preview: $preview
      where: {
        contentfulMetadata: {
          tags: { id_contains_some: ["${SITE_IDENTIFIER}"], id_contains_none: ["translation"] }
        }
      }
    ) {
      items {
        slug
        languageCode
        sys {
          publishedAt
        }
        seoMetadata {
          title
          description
        }
      }
    }
  }
`;
